import { Injectable, Injector } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { deepEqual } from 'fast-equals';
import { map, NEVER, of, zip } from 'rxjs';
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators';

import { AnalyticsSegmentClient } from './analytics-segment-client';
import { AnalyticsRouteData, Inherit } from './analytics.types';

export const inheritAnalytics = (
  ar: ActivatedRoute,
): AnalyticsRouteData['analytics'] | undefined => {
  if (!ar.parent) {
    return ar.snapshot.data?.analytics;
  }

  const isParentAnalyticsInherited = ar.parent.snapshot.data?.analytics === Inherit;

  if (isParentAnalyticsInherited) {
    return inheritAnalytics(ar.parent);
  }

  return ar.parent.snapshot.data?.analytics;
};

const findLastActivatedChild = (activatedRoute: ActivatedRoute) => {
  const lookup = (ar: ActivatedRoute): ActivatedRoute | null => {
    if (ar.firstChild) {
      return lookup(ar.firstChild);
    }

    return ar;
  };

  return lookup(activatedRoute);
};

@Injectable()
export class AnalyticsRouteTracker {
  constructor(private injector: Injector) {
    const router = injector.get(Router);
    const client = injector.get(AnalyticsSegmentClient);

    router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        switchMap(() => {
          const route = findLastActivatedChild(injector.get(ActivatedRoute));
          return route ? zip(route.data, route.params, of(route)) : NEVER;
        }),
        map(([data, params, route]) => {
          if (data.analytics !== Inherit) {
            return [data, params];
          }

          const inheritedAnalytics = inheritAnalytics(route);
          return [{ ...data, analytics: inheritedAnalytics }, params];
        }),
        filter(([data]) => !!data.analytics),
        distinctUntilChanged((a, b) => deepEqual(a[0].analytics, b[0].analytics)),
      )
      .subscribe(([data, params]) => {
        if (data.analytics.filter) {
          if (!data.analytics?.filter(params)) {
            return;
          }
        }

        client.page(data.analytics.category, data.analytics.name);
      });
  }
}
